import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import LandingNavbar from '../components/LandingNavbar';

/**
 * /forgot-password
 *
 * Public page. Collects an email and asks the backend to email a reset
 * link. The backend returns success regardless of whether the email
 * exists (avoids leaking account membership), so we always show the
 * "check your inbox" screen on success.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('يرجى إدخال بريدك الإلكتروني'); return; }

    setLoading(true);
    try {
      const axios = (await import('axios')).default;
      const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
      await axios.post(`${apiBase}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.message || 'تعذّر إرسال الطلب، حاول مرة أخرى');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LandingNavbar />
      <div
        className="min-h-screen flex items-center justify-center px-6 pt-[120px] pb-16 relative"
        dir="rtl"
        style={{ backgroundColor: '#FDFCF9' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[440px] bg-white rounded-3xl border border-[#E8E5DD] shadow-[0_10px_40px_-10px_rgba(15,110,86,0.12)] p-8 md:p-10"
        >
          <div className="flex justify-center mb-5">
            <img src="/listech-logo.svg" alt="LIS Techs" className="h-9 w-auto" />
          </div>

          {sent ? (
            <div className="text-center">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: '#E1F5EE' }}
              >
                <CheckCircle2 className="w-6 h-6" style={{ color: '#0F6E56' }} />
              </div>
              <h2 className="text-[1.3rem] font-bold mb-2" style={{ color: '#1A1A1A' }}>
                تحقّق من بريدك
              </h2>
              <p className="text-sm leading-[1.8]" style={{ color: '#666' }}>
                إن كان هذا البريد مسجلاً عندنا، سيصلك رابط إعادة تعيين كلمة المرور
                خلال دقيقة — تأكد من خانة الـSpam أيضاً.
              </p>
              <Link
                to="/staff-login"
                className="inline-flex items-center gap-1.5 text-sm font-medium mt-5"
                style={{ color: '#0F6E56' }}
              >
                العودة لتسجيل الدخول
                <ArrowRight className="w-4 h-4 rotate-180" />
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2
                  className="text-[1.5rem] font-bold mb-1"
                  style={{ color: '#1A1A1A', fontFamily: "'Readex Pro', sans-serif" }}
                >
                  نسيت كلمة المرور؟
                </h2>
                <p className="text-sm leading-[1.7]" style={{ color: '#8A8A8A' }}>
                  أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين
                </p>
              </div>

              {error && (
                <div
                  className="mb-5 p-3.5 rounded-xl flex items-center gap-3 text-sm border"
                  style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA', color: '#B91C1C' }}
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#444' }}>
                    البريد الإلكتروني
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-12 px-4 pr-11 rounded-xl text-sm border outline-none"
                      style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E5E5', color: '#1A1A1A' }}
                      onFocus={(e) => { e.target.style.borderColor = '#0F6E56'; e.target.style.boxShadow = '0 0 0 3px rgba(15,110,86,0.08)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#E5E5E5'; e.target.style.boxShadow = 'none'; }}
                      placeholder="example@lab.com"
                      dir="ltr"
                      autoFocus
                    />
                    <Mail className="absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px]" style={{ color: '#BFBFBF' }} />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl text-sm font-bold text-white"
                  style={{
                    background: 'linear-gradient(135deg, #0F6E56 0%, #1D9E75 100%)',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  {loading ? 'جارٍ الإرسال...' : 'إرسال رابط إعادة التعيين'}
                </button>

                <Link
                  to="/staff-login"
                  className="block text-center text-sm font-medium mt-2"
                  style={{ color: '#8A8A8A' }}
                >
                  العودة لتسجيل الدخول
                </Link>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </>
  );
}
